import type { IMenu, IMenuSection } from '@/types/menu';
import { FeatureFlagKey } from '@/features/feature-flag/feature-flag.constants';
import {
  CalendarRange,
  // CreditCard,
  Clipboard,
  LayoutDashboard,
  Package2,
  Table2,
  Coins,
  Calendar1,
  Building2,
  Users,
  MapPin,
  CalendarSync,
} from 'lucide-react';

export const menuSections: IMenuSection[] = [
  {
    id: 'main',
    menus: [
      {
        id: 0,
        icon: Calendar1,
        label: 'Today',
        key: 'today',
        route: '/today',
        feature: FeatureFlagKey.MODULE_TODAY,
      },
      {
        // The V2 design is now the only dashboard. The old `/dashboard` screen
        // was dropped from the menu and its URL redirects here, so the route
        // still resolves for anyone holding a bookmark or an old link.
        id: 31,
        icon: LayoutDashboard,
        label: 'Dashboard',
        key: 'dashboard',
        route: '/dashboard-v2',
        feature: FeatureFlagKey.MODULE_DASHBOARD,
      },
      {
        id: 1,
        icon: Table2,
        label: 'Planner',
        key: 'planner',
        route: '/planner',
      },
      {
        id: 2,
        icon: CalendarRange,
        label: 'Bookings',
        key: 'bookings',
        route: '/bookings',
      },
      {
        id: 4,
        icon: Clipboard,
        label: 'Schedule',
        route: '/schedule',
        children: [
          {
            id: 41,
            label: 'Opening Hours & Dates',
            key: 'opening-hour-and-dates',
            route: '/schedule/opening-hour-and-dates',
          },
          {
            id: 42,
            label: 'Closing Dates',
            key: 'closing-dates',
            route: '/schedule/closing-dates',
          },
          {
            id: 43,
            label: 'Availability',
            key: 'availability',
            route: '/schedule/availability',
            feature: FeatureFlagKey.RELEASE_SCHEDULE_AVAILABILITY,
          },
        ],
      },
      {
        id: 5,
        icon: Coins,
        label: 'Pricing',
        route: '/pricing',
        children: [
          // {
          //   id: 51,
          //   label: 'Discount Categories',
          //   key: 'discount-categories',
          //   route: '/pricing/discount-categories',
          // },
          {
            id: 53,
            label: 'Coupons',
            key: 'coupons',
            route: '/pricing/coupons',
            feature: FeatureFlagKey.MODULE_COUPONS,
          },
          {
            id: 54,
            label: 'Gift Cards',
            key: 'gift-cards',
            route: '/pricing/gift-cards',
            feature: FeatureFlagKey.MODULE_GIFT_CARDS,
          },
          {
            id: 52,
            label: 'Smart Pricing',
            key: 'smart-pricing',
            route: '/pricing/smart-pricing',
            feature: FeatureFlagKey.MODULE_SMART_PRICING,
          },
          {
            id: 55,
            label: 'Smart Pricing V2',
            // the old page's permissions: the backend grants no other key
            key: 'smart-pricing',
            route: '/pricing/smart-pricing-v2',
            feature: FeatureFlagKey.RELEASE_PRICING_SMART_PRICING,
          },
        ],
      },
      {
        id: 6,
        icon: Package2,
        label: 'Inventory',
        route: '/inventory',
        children: [
          {
            id: 61,
            label: 'Resources',
            key: 'resources',
            route: '/inventory/resources',
          },
          {
            id: 62,
            label: 'Activities',
            key: 'activities',
            route: '/inventory/activities',
          },
          {
            id: 63,
            label: 'Add-Ons',
            key: 'add-ons',
            route: '/inventory/add-ons',
            feature: FeatureFlagKey.MODULE_ADD_ONS,
          },
          {
            id: 64,
            label: 'Food & Beverage',
            key: 'food-and-beverage',
            route: '/inventory/food-and-beverage',
            feature: FeatureFlagKey.MODULE_FOOD_AND_BEVERAGE,
          },
          {
            id: 65,
            label: 'Smart Suggestion',
            key: 'smart-suggestion',
            route: '/inventory/smart-suggestion',
            feature: FeatureFlagKey.MODULE_SMART_SUGGESTION,
          },
        ],
      },
      // {
      //   id: 7,
      //   icon: CreditCard,
      //   label: 'Payment',
      //   key: 'payment',
      //   route: '/payment',
      // },
      // {
      //   id: 8,
      //   icon: Package2,
      //   label: 'User & Access',
      //   route: '/user-access',
      //   children: [
      //     {
      //       id: 81,
      //       label: 'Employee Management',
      //       key: 'employeeManagement',
      //       route: '/user-access/employee-management',
      //     },
      //     // {
      //     //   id: 72,
      //     //   label: 'Role Management',
      //     //   key: 'roleManagement',
      //     //   route: '/user-access/role-management',
      //     // },
      //     // {
      //     //   id: 83,
      //     //   label: 'Location',
      //     //   key: 'locationManagement',
      //     //   route: '/user-access/location',
      //     // },
      //   ],
      // },
    ],
  },
  {
    id: 'setting',
    label: 'Settings',
    menus: [
      {
        id: 9,
        icon: Building2,
        label: 'Company',
        key: 'settings',
        route: '/setting/company/general',
        tabs: true,
        children: [
          // The company-detail record (General, Betaaloverzicht and Gift
          // card) on one page with one Save, as the API stores it.
          // `group` draws these as a grouped side nav (`SectionTabsFrame`).
          {
            id: 91,
            label: 'Details',
            key: 'general',
            route: '/setting/company/general',
            group: 'Company',
          },
          // Full / partial payment on the payment page and in the widget. Its
          // own record, saved per switch, under the company detail permission.
          {
            id: 911,
            label: 'Payment options',
            key: 'general',
            route: '/setting/company/payment-options',
            group: 'Company',
          },
          {
            id: 910,
            label: 'Promo banner',
            key: 'promoBanner',
            route: '/setting/company/promo-banner',
            feature: FeatureFlagKey.MODULE_PROMO_BANNER,
            group: 'Company',
          },
          // The quick-pick amounts, offered in the widget and on the CMS gift
          // card form (ELI-345).
          {
            id: 94,
            label: 'Values',
            key: 'giftCardValueSetting',
            route: '/setting/company/gift-card-values',
            feature: FeatureFlagKey.MODULE_GIFT_CARDS,
            group: 'Gift cards',
          },
          // Tax Setting moved to the Superadmin Console (Console → Tax
          // Setting): what tax applies is platform policy, not a tenant's.
          {
            id: 95,
            label: 'Resellers',
            key: 'resellerSetting',
            route: '/setting/company/reseller-setting',
            group: 'Bookings',
            feature: FeatureFlagKey.MODULE_RESELLERS,
          },
          {
            id: 96,
            label: 'Group types',
            key: 'groupTypeSetting',
            route: '/setting/company/group-type-setting',
            group: 'Bookings',
            feature: FeatureFlagKey.MODULE_GROUP_TYPES,
          },
          // Quick picks for the activity Pricing tab.
          {
            id: 97,
            label: 'Age groups',
            key: 'ageGroupPreset',
            route: '/setting/company/age-groups',
            group: 'Bookings',
          },
          {
            id: 98,
            label: 'Group sizes',
            key: 'groupSizePreset',
            route: '/setting/company/group-sizes',
            group: 'Bookings',
          },
        ],
      },

      {
        id: 10,
        icon: MapPin,
        label: 'Location',
        key: 'locationManagement',
        route: '/user-access/location',
      },

      {
        id: 11,
        icon: CalendarSync,
        label: 'Google Calendar',
        key: 'googleCalendar',
        route: '/google-calendar',
      },

      // One page, so a plain link rather than a one-item dropdown. When Role
      // Management returns, give this entry `tabs: true` and two children.
      {
        id: 8,
        icon: Users,
        label: 'User & Access',
        key: 'employeeManagement',
        route: '/user-access/employee-management',
      },
    ],
  },
];

export const menus: IMenu[] = menuSections.flatMap(section => section.menus);
